// Who may grant what at one customer, and who may add stores, manage regions
// or edit a store there; kept free of React so tests can load it. Mirrors
// can_add_customer_user, can_manage_customer_locations and can_manage_location
// in api/user_customer/access.py; both sides run the shared cases in
// accessRuleCases.json (src/lib/accessRules.test.ts here,
// api/user_customer/test_access_rule_cases.py there).
import { User, UserCustomerRef } from '@/types/user';

export const SERVICE_ROLES = ['Service 1 Users', 'Service 2 Users'];

export const isAdminUser = (user?: User | null) =>
  !!user && (user.is_superuser || user.groups.includes('Admins'));
export const isServiceUser = (user?: User | null) =>
  !!user && user.groups.some((g) => SERVICE_ROLES.includes(g));

// What a user may grant at one customer. Mirrors can_add_customer_user in
// api/user_customer/access.py: Admins, superusers and fleet-linked Service
// users may grant anything; other Service users only locations and regions
// inside their own link; everyone else nothing.
export type GrantLimits = {
  canGrant: boolean;
  allowFleet: boolean;
  canGrantLocation: (locationId: string) => boolean;
  canGrantRegion: (regionId: string) => boolean;
};

const FULL: GrantLimits = {
  canGrant: true,
  allowFleet: true,
  canGrantLocation: () => true,
  canGrantRegion: () => true,
};
const NONE: GrantLimits = {
  canGrant: false,
  allowFleet: false,
  canGrantLocation: () => false,
  canGrantRegion: () => false,
};

export const grantLimits = (
  user: User | null | undefined,
  customerId: string,
  locations: { id: string; region?: string | null }[] = []
): GrantLimits => {
  const link = user?.customers?.find((c) => c.id === customerId);
  if (isAdminUser(user) || (isServiceUser(user) && link?.scope === 'fleet')) return FULL;
  if (!isServiceUser(user) || !link) return NONE;
  if (link.scope === 'locations') {
    const mine = new Set(link.scoped_locations.map((l) => l.id));
    return {
      canGrant: true,
      allowFleet: false,
      canGrantLocation: (id) => mine.has(id),
      canGrantRegion: (regionId) => {
        const inRegion = locations.filter((l) => l.region === regionId);
        return inRegion.length > 0 && inRegion.every((l) => mine.has(l.id));
      },
    };
  }
  if (link.scope === 'regions') {
    const mine = new Set(link.scoped_regions.map((r) => r.id));
    return {
      canGrant: true,
      allowFleet: false,
      canGrantLocation: (id) => {
        const region = locations.find((l) => l.id === id)?.region;
        return !!region && mine.has(region);
      },
      canGrantRegion: (regionId) => mine.has(regionId),
    };
  }
  // A "no access" link: linked, but nothing to hand on.
  return { ...NONE, canGrant: true };
};

export const linkWithin = (limits: GrantLimits, link: UserCustomerRef) => {
  if (link.scope === 'none') return limits.canGrant;
  if (link.scope === 'fleet') return limits.allowFleet;
  if (link.scope === 'locations') return link.scoped_locations.every((l) => limits.canGrantLocation(l.id));
  if (link.scope === 'regions') return link.scoped_regions.every((r) => limits.canGrantRegion(r.id));
  return false;
};

// Who may add/edit/delete locations and regions for a customer. Admins and
// superusers for every customer; Service users for customers they're linked
// to: fleet scope to add locations or manage regions, and only locations
// inside their scope to edit. Everyone else can look but not change, so the
// controls are hidden rather than left to fail with a 403.
export const locationLimits = (user: User | null | undefined, customerId: string) => {
  const isAdmin = isAdminUser(user);
  const isService = isServiceUser(user);
  const link = user?.customers?.find((c) => c.id === customerId);

  const canManageCustomer = isAdmin || (isService && link?.scope === 'fleet');

  const canEditLocation = (location: { id: string; region?: string | null }) => {
    if (canManageCustomer) return true;
    if (!isService || !link) return false;
    if (link.scope === 'locations') {
      return link.scoped_locations.some((l) => l.id === location.id);
    }
    if (link.scope === 'regions') {
      return !!location.region && link.scoped_regions.some((r) => r.id === location.region);
    }
    return false;
  };

  return { canManageCustomer, canEditLocation };
};
